import { useState, useRef, useEffect } from "react";
import { DownloadIcon, EyeOffIcon } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import { Button } from "@/components/ui/button";

// We follow the instructions from the react-pdf documentation:
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#use-external-cdn
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#usage

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function DocumentPreview({ href }: { href: string }) {
  const [numPages, setNumPages] = useState<number>(1);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    setWidth(containerRef.current.clientWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const unsupportedExtensions = [".doc", ".docx"];
  const unsupportedExtension = unsupportedExtensions.find((ext) =>
    href.endsWith(ext),
  );

  return (
    <div ref={containerRef}>
      {unsupportedExtension ? (
        <div className="flex flex-col items-center gap-6 p-12">
          <EyeOffIcon className="h-20 w-20 stroke-[1px] text-slate-700" />
          <div className="flex flex-col items-center gap-4">
            <p className="text-lg font-semibold text-slate-700">
              Resume not readable
            </p>
            <p className="text-center font-medium text-slate-700">
              If you would like to view this candidate&apos;s resume, you must
              download it below
            </p>
            <Button className="rounded-full" variant="outline" asChild>
              <a href={href} rel="noreferrer" target="_blank" download>
                <DownloadIcon className="h-4 w-4" />
                Download resume
              </a>
            </Button>
          </div>
        </div>
      ) : (
        <Document file={href} onLoadSuccess={onDocumentLoadSuccess}>
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              className="[&:not(:last-child)]:border-b [&:not(:last-child)]:border-slate-200"
              renderAnnotationLayer={false}
              renderTextLayer={false}
              // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={width}
            />
          ))}
        </Document>
      )}
    </div>
  );
}

export default DocumentPreview;
